import React from 'react';
import { Box, Modal, Tooltip, Typography } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { Secondary } from 'ui-components/Buttons';

import {
  FetchAllCountries,
  FetchAllCountriesWithPhoneCodes,
  FetchAllInvestorTypes,
} from 'api/utils';
import {
  AddPartners,
  FetchProfileDetails,
  SavePersonalInfo,
  SaveProfileImage,
  SendWhatsappOTP,
  VerifyPublicURL,
  VerifyWhatsAppOTP,
} from 'api/profile';
import { useToast } from 'context/ToastContext';
import { checkForAuthorization, extractMainDomain, handleFormSubmit, websiteTestURL } from 'utils/index';
import BackrrLogo from 'assets/backrr_logo.png';
import Wrapper from 'views/onboarding/Wrapper';
import MixpanelService, { getUtmProperties } from '../../mixpanel';

import BasicDetails from './steps/BasicDetails';
import InvestmentInterest from './steps/InvestmentInterest';
import PartnerInformation, { isPartnerComplete } from './steps/PartnerInformation';
import { BORDER, INDIGO, MUTED, NextButton, PAGE_BG, Stepper } from './primitives';

// The old wizard resumes from the `step` cookie. v2 keeps its own so the two
// flows can run side by side without corrupting each other's progress.
const STEP_COOKIE = 'step_v2';

const isAngelType = (investorType) =>
  investorType ? String(investorType).toLowerCase().includes('angel') : true;

// Angel/Super Angel run a 2-step flow, every other type a 3-step one — so the
// investor type decides whether Step 2 completes onboarding. It must never be read
// from an empty value: FetchProfileDetails maps `investor_type: '' ` when the API
// omits it, and enableReinitialize then blanks formik's copy on every refetch.
// Combined with isAngelType's "" => Angel default, a VC finished at Step 2 and never
// saw Partners. So the type chosen on Step 1 is also cookied, and survives a reload.
const TYPE_COOKIE = 'investor_type_v2';

// Step labels for analytics — parity with the old onboarding's step naming.
const STEP_NAMES = { 1: 'Basic Details', 2: 'Investment Interest', 3: 'Partner Information' };

const OnboardingV2 = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [investor_id, setInvestorID] = React.useState(Cookies.get('X-INV-ID') || null);
  const [currentStep, setCurrentStep] = React.useState(() => Number(Cookies.get(STEP_COOKIE)) || 1);
  // "All" is how the backend records sector-agnostic / every-business-model, and it
  // is expressed by an EMPTY array plus type "All" — same as the old wizard.
  const [isSectorAgnostic, setIsSectorAgnostic] = React.useState(false);
  const [isBusinessModelSelectAll, setIsBusinessModelSelectAll] = React.useState(false);
  // Last known investor type — survives a blank refetch and a page reload.
  const lastKnownInvestorType = React.useRef(Cookies.get(TYPE_COOKIE) || '');
  const resolveInvestorType = (investorType) => {
    if (investorType) lastKnownInvestorType.current = investorType;
    return investorType || lastKnownInvestorType.current;
  };

  // Base analytics props — mirrors the old onboarding's trackStepCompleted base.
  const trackBase = (values) => ({
    step: currentStep,
    step_name: STEP_NAMES[currentStep] || `Step ${currentStep}`,
    investor_type: values?.investor_type || null,
    investor_id: Cookies.get('X-INV-ID') || investor_id || null,
    ...getUtmProperties(),
  });

  const { data: profileDetailsData, isLoading: isProfileDetailsLoading, refetch: refetchProfileDetails } =
    FetchProfileDetails({ investor_id });
  const { data: investorTypes, isLoading: isLoadingInvestorTypes } = FetchAllInvestorTypes({
    investor_id: null,
  });
  const { isLoading: isLoadingCountries } = FetchAllCountries({ search: '' });
  const { data: phoneCodes } = FetchAllCountriesWithPhoneCodes({});

  const { mutateAsync: SavePersonalInfoAsync, isLoading: isUpdatingPersonalInfo } =
    SavePersonalInfo();
  const { mutateAsync: SaveProfileImageAsync, isLoading: isUpdatingProfileImage } = SaveProfileImage();
  const { mutateAsync: AddPartnersAsync, isLoading: isUpdatingPartners } = AddPartners();
  const { mutateAsync: SendWhatsappOTPAsync, isLoading: isSendingOTP } = SendWhatsappOTP();
  const { mutateAsync: VerifyWhatsAppOTPAsync, isLoading: isVerifyingOTP } = VerifyWhatsAppOTP();
  const { mutateAsync: verifyPublicURLAsync } = VerifyPublicURL();

  // Same file rules the old wizard applied in profileImageValidations /
  // organizationImageValidations: jpg/png/jpeg, 10MB max.
  const imageValidation = Yup.mixed()
    .test('file-or-url', 'Invalid image', function (value) {
      const { path, createError } = this;
      if (value && value instanceof File) {
        const validFileTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validFileTypes.includes(value.type)) {
          return createError({
            path,
            message: 'Invalid file type. Only JPG, PNG, or JPEG are allowed.',
          });
        }
        if (value.size > 10000000) {
          return createError({ path, message: 'File size exceeds 10MB.' });
        }
      }
      return true;
    })
    .nullable();

  // Validation is per step — only the fields on screen are checked.
  const validationSchema = () => {
    const angel = isAngelType(resolveInvestorType(formik?.values?.investor_type));

    // Partners are optional, and incomplete rows are filtered out on submit.
    if (currentStep === 3) return Yup.object().shape({});

    if (currentStep === 2) {
      return Yup.object().shape({
        category_id: Yup.array()
          .of(Yup.string().trim())
          .min(1, 'Please select at least one category')
          .required('Please select your preferred category'),
        sector_id: isSectorAgnostic
          ? Yup.array().nullable()
          : Yup.array().min(1, 'Please select your sub sector').required('Please select your sub sector'),
        business_model_id: isBusinessModelSelectAll
          ? Yup.array().nullable()
          : Yup.array().min(1, 'Business Model is required.').required('Business Model is required.'),
        min: Yup.string().nullable().required('Please select an investment range'),
        max: Yup.string().nullable().required('Please select an investment range'),
        stage_id: Yup.array().min(1, 'Please select your preferred stages'),
      });
    }

    const organizationRules = angel
      ? {}
      : {
          organization_name: Yup.string().required('Organization name is required').trim(),
          // Website is optional now — but if provided, it must still be a valid URL.
          organization_website: Yup.string()
            .trim()
            .matches(websiteTestURL, { message: 'Please enter a valid website URL', excludeEmptyString: true })
            .nullable(),
        };

    return Yup.object().shape({
      title: Yup.string().required('Please select a title').trim(),
      // A person's name cannot contain digits. Deliberately scoped to the investor's
      // own name - organization_name above stays unrestricted, because company names
      // legitimately contain numbers ("3one4 Capital", "Y Combinator 2024").
      // The pattern only rejects 0-9, so accents, hyphens, apostrophes and
      // non-Latin scripts all still pass.
      first_name: Yup.string().required('Please enter your first name').trim()
        .matches(/^[^0-9]*$/, 'First name cannot contain numbers'),
      last_name: Yup.string().required('Please enter your last name').trim()
        .matches(/^[^0-9]*$/, 'Last name cannot contain numbers'),
      investor_type: Yup.string().required('Please select investor type').trim(),
      phone_number: Yup.string()
        .required(angel ? 'Please enter contact number' : 'Please enter office contact number')
        .trim()
        // Indian mobile numbers must be exactly 10 digits
        .test('india-10-digits', 'Please enter a valid 10-digit mobile number', function (value) {
          if (this.parent?.country_code === '91') {
            return /^\d{10}$/.test(value || '');
          }
          return true;
        }),
      dd: Yup.string().matches(/^(0[1-9]|[12][0-9]|3[01])$/, 'Please enter a valid date'),
      mm: Yup.string().matches(/^(0[1-9]|1[0-2])$/, 'Please enter a valid month'),
      yyyy: Yup.number()
        .min(1900, 'Please enter a valid year')
        .max(new Date().getFullYear(), 'Date of birth cannot be in the future'),
      country_id: Yup.string().required('Please select your country').nullable(),
      custom_image_url: imageValidation,
      website_logo: imageValidation,
      ...organizationRules,
    });
  };

  const formik = useFormik({
    initialValues:
      !isProfileDetailsLoading && profileDetailsData
        ? // Figma defaults the title to "Mr."; a saved profile keeps whatever it has.
          { ...profileDetailsData.data, title: profileDetailsData.data?.title || 'Mr.' }
        : {
            investor_type: '',
            title: 'Mr.',
            first_name: '',
            last_name: '',
            dd: '',
            mm: '',
            yyyy: '',
            bio: '',
            linkedIn_profile_url: '',
            organization_name: '',
            organization_website: '',
            country_code: '91',
            phone_number: '',
            custom_image_url: null,
            website_logo: null,
            country_id: null,
            state_id: null,
            city_id: null,
            enabled: false,
            phone_code: '91',
            whatsapp_number: '',
            otp_verified: false,
            public_url: '',
            profile_view: true,
            // Step 2
            category_id: [],
            sector_id: [],
            business_model_id: [],
            preferred_country_id: [],
            preferred_state_id: [],
            preferred_city_id: [],
            investment_size: null,
            min: null,
            max: null,
            investment_amount_type: null,
            stage_id: [],
            investment_count: null,
            // Step 3
            partners: [{ name: '', title: '', linkedin_url: '', bio: '', image: null }],
          },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const investorType = resolveInvestorType(values?.investor_type);
      const angel = isAngelType(investorType);

      // ------------------------------- Step 3 -------------------------------
      // Org types only. Partners are optional; only complete ones are sent, and
      // onboarding completes either way.
      if (currentStep === 3) {
        const completePartners = (values.partners || []).filter(isPartnerComplete);

        try {
          if (completePartners.length > 0) {
            // Exactly the FormData shape the old wizard's step 13 builds.
            const formData = new FormData();
            completePartners.forEach((partner, index) => {
              formData.append(`partner[${index}][name]`, partner?.name);
              formData.append(`partner[${index}][linkedin_url]`, `https://${partner?.linkedin_url}`);
              if (typeof partner?.image === 'string') {
                formData.append(`partner[${index}][flag]`, 'true');
              } else {
                formData.append(`partner${index}`, partner?.image);
              }
              formData.append(`partner[${index}][bio]`, partner?.bio);
              formData.append(`partner[${index}][title]`, partner?.title);
            });
            await AddPartnersAsync(formData);
          }

          await SavePersonalInfoAsync({
            ...(investor_id ? { investor_id } : {}),
            is_profile_completed: true,
          });

          Cookies.set('is_profile_complete', true);
          Cookies.remove(STEP_COOKIE, { path: '/' });
          Cookies.remove(TYPE_COOKIE, { path: '/' });
          MixpanelService.track('Onboarding - Partner Information Completed', {
            ...trackBase(values),
            partners_count: completePartners.length,
          });
          MixpanelService.track('Onboarding Completed', trackBase(values));
          // Full load, not a client-side navigate: ThemeRoutes picks the route tree from
          // the is_profile_complete cookie during render, and the sidebar labels come from
          // permissions/investor state fetched at app start. Setting the cookie mid-session
          // leaves both stale, which is why the user had to refresh by hand before Deal Flow
          // and the sidebar showed correct data. Landing on "/" also puts them on Deals.
          window.location.replace('/');
        } catch (err) {
          addToast(err?.response?.data?.message, 'error');
        }
        return;
      }

      // ------------------------------- Step 2 -------------------------------
      // Angel / Super Angel finish here (2-step flow); org types go on to Partners.
      // Only complete onboarding when the type is actually known — never on a blank.
      if (currentStep === 2) {
        const isLastStep = angel && !!investorType;
        const request = {
          ...(investor_id ? { investor_id } : {}),
          category_id: values.category_id,
          sector_ids: values.sector_id,
          sector_type: values.sector_id?.length > 0 ? 'Multiple' : 'All',
          business_model_id: values.business_model_id,
          business_model_type: values.business_model_id?.length > 0 ? 'Multiple' : 'All',
          preferred_country_ids: values.preferred_country_id || [],
          preferred_state_ids: values.preferred_state_id || [],
          preferred_city_ids: values.preferred_city_id || [],
          investment_size: values.investment_size,
          min: values.min,
          max: values.max,
          investment_amount_type: values.investment_amount_type,
          stage_ids: values.stage_id,
          investment_count: values.investment_count,
          ...(isLastStep
            ? { is_profile_completed: true, profile_view: values.profile_view ?? true }
            : { step: 3 }),
        };

        try {
          await SavePersonalInfoAsync(request);
          refetchProfileDetails();
          MixpanelService.track('Onboarding - Investment Interest Completed', {
            ...trackBase(values),
            categories_count: values.category_id?.length || 0,
            sectors_count: values.sector_id?.length || 0,
            business_models_count: values.business_model_id?.length || 0,
            stages_count: values.stage_id?.length || 0,
          });
          if (isLastStep) {
            Cookies.set('is_profile_complete', true);
            Cookies.remove(STEP_COOKIE, { path: '/' });
          Cookies.remove(TYPE_COOKIE, { path: '/' });
            MixpanelService.track('Onboarding Completed', trackBase(values));
            // Full load, not a client-side navigate: ThemeRoutes picks the route tree from
          // the is_profile_complete cookie during render, and the sidebar labels come from
          // permissions/investor state fetched at app start. Setting the cookie mid-session
          // leaves both stale, which is why the user had to refresh by hand before Deal Flow
          // and the sidebar showed correct data. Landing on "/" also puts them on Deals.
          window.location.replace('/');
          } else {
            setCurrentStep(3);
            Cookies.set(STEP_COOKIE, 3);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (err) {
          addToast(err?.response?.data?.message, 'error');
        }
        return;
      }

      // ------------------------------- Step 1 -------------------------------

      let dob = null;
      if (values.mm && values.dd && values.yyyy) {
        dob = moment(`${values.mm}-${values.dd}-${values.yyyy}`, 'MM-DD-YYYY');
      }
      const utm_campaign = Cookies.get('utm_campaign');

      // Same payload keys the old wizard sends — steps 2..6 of the old flow,
      // merged into one request.
      const request = {
        ...(investor_id ? { investor_id } : {}),
        ...(utm_campaign ? { utm_campaign } : {}),
        title: values.title,
        first_name: values.first_name,
        last_name: values.last_name,
        dob,
        bio: values.bio,
        investor_type: values.investor_type,
        linkedin_profile_url: values.linkedIn_profile_url,
        phone_number: values.phone_number,
        country_id: values.country_id,
        state_id: values.state_id,
        city_id: values.city_id,
        profile_view: values.profile_view ?? true,
        step: 2,
        ...(angel
          ? {}
          : {
              organization_name: values.organization_name,
              country_code: values.country_code,
              organization_website: values.organization_website,
              domain_name: extractMainDomain(values.organization_website),
            }),
      };

      try {
        const response = await SavePersonalInfoAsync(request);
        // Remember the chosen type: the profile refetch below can hand back an empty
        // investor_type, and enableReinitialize would blank formik's copy.
        if (values.investor_type) Cookies.set(TYPE_COOKIE, values.investor_type);

        const newInvestorId = response?.data?.data?._id;
        if (newInvestorId) {
          Cookies.set('X-INV-ID', newInvestorId);
          setInvestorID(newInvestorId);
        }
        if (utm_campaign) Cookies.remove('utm_campaign');

        const image = angel ? values.custom_image_url : values.website_logo;
        if (image && typeof image !== 'string') {
          const formData = new FormData();
          formData.append(angel ? 'profile_picture' : 'website_logo', image);
          formData.append('step', 2);
          await SaveProfileImageAsync(formData).catch((err) =>
            addToast(err?.response?.data?.message, 'error')
          );
        }

        // Same silent public-URL slug generation as the old wizard.
        await verifyWithRetries(
          angel
            ? String(`${values.first_name}${values.last_name}`)
                .toLowerCase()
                .replaceAll(' ', '')
                .replace(/[^a-zA-Z0-9]/g, '')
            : String(values.organization_name || '')
                .toLowerCase()
                .replaceAll(' ', '')
                .replace(/[^a-zA-Z0-9]/g, ''),
          newInvestorId || investor_id
        );

        refetchProfileDetails();
        MixpanelService.track('Onboarding - Basic Details Completed', {
          ...trackBase(values),
          investor_type: values.investor_type,
        });
        setCurrentStep(2);
        Cookies.set(STEP_COOKIE, 2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        addToast(err?.response?.data?.message, 'error');
      }
    },
  });

  const verifyWithRetries = async (initialPublicURL, id) => {
    if (!initialPublicURL || initialPublicURL.trim() === '') return;

    let count = 0;
    let isVerified = false;
    let publicURL = initialPublicURL;

    while (!isVerified && count <= 10) {
      try {
        await verifyPublicURLAsync({ slug: publicURL, investor_id: id });
        formik.setFieldValue('isPublicURLVerify', true);
        formik.setFieldValue('public_url', publicURL);
        isVerified = true;
      } catch (err) {
        count++;
        publicURL = `${initialPublicURL}0${count}`;
      }
    }
  };

  // ------------------------------ WhatsApp (optional) ------------------------------
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpError, setOtpError] = React.useState(null);
  const [otpValues, setOtpValues] = React.useState(Array(4).fill(''));
  const otpRefs = [React.useRef(null), React.useRef(null), React.useRef(null), React.useRef(null)];

  const resetOTP = () => {
    setOtpSent(false);
    setOtpError(null);
    setOtpValues(Array(4).fill(''));
  };

  const whatsapp = {
    isSending: isSendingOTP,
    isVerifying: isVerifyingOTP,
    otpSent,
    error: otpError,
    inputRefs: otpRefs,
    reset: resetOTP,
    sendOTP: async () => {
      try {
        await SendWhatsappOTPAsync({
          ...(investor_id ? { investor_id } : {}),
          phone_code: formik.values.phone_code,
          mobile_number: formik.values.whatsapp_number,
        });
        setOtpSent(true);
        setOtpError(null);
      } catch (err) {
        addToast(err?.response?.data?.message, 'error');
      }
    },
    verifyOTP: async () => {
      const otp = otpValues.join('');
      if (!otp.trim()) {
        setOtpError('Please enter OTP');
        return;
      }
      try {
        const res = await VerifyWhatsAppOTPAsync({ enabled: formik.values.enabled, otp });
        if (res?.data?.statusCode === 200) {
          formik.setFieldValue('otp_verified', true);
          resetOTP();
          refetchProfileDetails();
        } else {
          setOtpError(res?.data?.message || 'Invalid OTP');
        }
      } catch (err) {
        setOtpError(err?.response?.data?.message || 'Invalid OTP');
      }
    },
    handleOtpChange: (event, index) => {
      const { value } = event.target;
      const next = [...otpValues];
      if (value.length <= 1) next[index] = value;
      setOtpValues(next);
      setOtpError(null);
      if (value.length === 1 && index < otpRefs.length - 1) otpRefs[index + 1].current?.focus();
    },
    handleOtpKeyDown: (event, index) => {
      if (event.key === 'Backspace' && !event.target.value && index > 0) {
        otpRefs[index - 1].current?.focus();
      }
      if (event.key === 'ArrowRight' && index < otpRefs.length - 1) {
        otpRefs[index + 1].current?.focus();
      }
      if (event.key === 'ArrowLeft' && index > 0) otpRefs[index - 1].current?.focus();
    },
    handleOtpPaste: (event) => {
      event.preventDefault();
      const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
      const next = [...otpValues];
      pasted.split('').forEach((char, index) => {
        if (otpRefs[index]?.current) otpRefs[index].current.value = char;
        next[index] = char;
      });
      setOtpValues(next);
    },
  };

  // A saved profile records sector-agnostic / all-business-models as type "All".
  React.useEffect(() => {
    if (isProfileDetailsLoading) return;
    if (profileDetailsData?.data?.sector_type === 'All') setIsSectorAgnostic(true);
    if (profileDetailsData?.data?.business_model_type === 'All') setIsBusinessModelSelectAll(true);
  }, [isProfileDetailsLoading, profileDetailsData]);

  // ---- Mixpanel analytics — parity with the old onboarding (all calls no-op when disabled) ----
  React.useEffect(() => {
    const id = Cookies.get('X-INV-ID');
    if (id) MixpanelService.identify(id);
    MixpanelService.track('Onboarding Started', {
      step: currentStep,
      step_name: STEP_NAMES[currentStep] || `Step ${currentStep}`,
      investor_id: id || null,
      ...getUtmProperties(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    MixpanelService.track('Onboarding Step Viewed', {
      step: currentStep,
      step_name: STEP_NAMES[currentStep] || `Step ${currentStep}`,
      investor_type: formik.values?.investor_type || null,
      ...getUtmProperties(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  React.useEffect(() => {
    if (formik.values?.investor_type) {
      MixpanelService.track('Investor Type Selected', {
        investor_type: formik.values.investor_type,
        ...getUtmProperties(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.investor_type]);

  const angel = isAngelType(resolveInvestorType(formik.values.investor_type));
  const steps = angel
    ? ['Basic Details', 'Investment Interest']
    : ['Basic Details', 'Investment Interest', 'Partner Information'];

  const isSubmitting = isUpdatingPersonalInfo || isUpdatingProfileImage || isUpdatingPartners;

  return (
    <Box sx={{ minHeight: '100dvh', background: PAGE_BG, display: 'flex', flexDirection: 'column' }}>
      <Modal open={isProfileDetailsLoading || isLoadingCountries || isLoadingInvestorTypes}>
        <Wrapper
          className="skeleton"
          style={{ display: 'grid', height: '100vh', width: '100vw', placeItems: 'center' }}
        />
      </Modal>

      {/* Top bar — sticky, solid bg so the form scrolls under it (his index.js:1102) */}
      <Box
        sx={{
          background: PAGE_BG,
          px: { xs: 2.5, sm: 5 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <img
          src={BackrrLogo}
          alt="Backrr"
          style={{ height: '34px', width: 'auto', objectFit: 'contain' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: INDIGO }}>
            <ShieldCheck size={18} strokeWidth={2} />
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: INDIGO }}>
              Secure &amp; Private
            </Typography>
          </Box>
          {checkForAuthorization() && (
            <Tooltip title="Logout">
              <Box
                role="button"
                onClick={() => navigate('/logout')}
                sx={{ display: 'flex', cursor: 'pointer', color: MUTED, p: 1 }}
              >
                <LogOut size={20} strokeWidth={1.5} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Stepper steps={steps} currentStep={currentStep} />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 1.5, sm: 3 },
          py: { xs: 2.5, sm: 4 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            background: '#fff',
            border: '1px solid #EFEFF2',
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
            p: { xs: 2.5, sm: 5 },
          }}
        >
          <FormikProvider value={formik}>
            <Form
              onSubmit={(e) => handleFormSubmit(e, formik)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) e.preventDefault();
              }}
            >
              {currentStep === 1 && (
                <BasicDetails
                  isAngel={angel}
                  investorTypes={investorTypes}
                  phoneCodes={phoneCodes}
                  whatsapp={whatsapp}
                />
              )}

              {currentStep === 2 && (
                <InvestmentInterest
                  isSectorAgnostic={isSectorAgnostic}
                  setIsSectorAgnostic={setIsSectorAgnostic}
                  isBusinessModelSelectAll={isBusinessModelSelectAll}
                  setIsBusinessModelSelectAll={setIsBusinessModelSelectAll}
                />
              )}

              {currentStep === 3 && <PartnerInformation />}

              <Box
                sx={{
                  mt: { xs: 3, sm: 4 },
                  pt: 3,
                  borderTop: `1px solid ${BORDER}`,
                  display: 'flex',
                  justifyContent: currentStep > 1 ? 'space-between' : 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {currentStep > 1 && (
                  <Secondary
                    onClick={() => {
                      const previous = currentStep - 1;
                      setCurrentStep(previous);
                      Cookies.set(STEP_COOKIE, previous);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    sx={{ px: { xs: 2, sm: 3 }, py: 1.6, minWidth: { xs: '100px', sm: '140px' } }}
                  >
                    Back
                  </Secondary>
                )}
                <NextButton
                  loading={isSubmitting}
                  // Angel / Super Angel finish on step 2 (no Partners step); org types on step 3.
                  label={currentStep === steps.length ? 'Get Started' : 'Next'}
                  // In the Figma only Step 1's "Next" carries an arrow.
                  showArrow={currentStep === 1}
                  onClick={formik.handleSubmit}
                />
              </Box>
            </Form>
          </FormikProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingV2;
